import React, { useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  X, Upload, Camera, AlertTriangle, Droplets, 
  Flame, Users, Activity, HelpCircle, RefreshCw,
  CheckCircle, XCircle, FileWarning
} from 'lucide-react';
import { analyzeImage, AnalysisResult } from '../services/gemini';

interface ImageAnalysisModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSendToRescue?: (result: AnalysisResult) => void;
}

const ImageAnalysisModal: React.FC<ImageAnalysisModalProps> = ({ 
  isOpen, 
  onClose, 
  onSendToRescue 
}) => {
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisResult, setAnalysisResult] = useState<AnalysisResult | null>(null);
  const [dragActive, setDragActive] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [analysisStage, setAnalysisStage] = useState<'idle' | 'analyzing' | 'complete' | 'error'>('idle');

  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback(async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    const file = e.dataTransfer.files?.[0];
    if (file) {
      await processImageFile(file);
    }
  }, []);

  const handleImageSelect = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      await processImageFile(file);
    }
  }, []);

  const processImageFile = async (file: File) => {
    // Simple validation
    if (file.size > 10 * 1024 * 1024) {
      setError('Image too large. Maximum size is 10MB.');
      return;
    }

    const validTypes = ['image/jpeg', 'image/png', 'image/jpg', 'image/gif', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      setError('Invalid file type. Please upload JPEG, PNG, GIF, or WEBP.');
      return;
    }

    setError(null);
    setSelectedFile(file);
    
    // Create preview
    const reader = new FileReader();
    reader.onload = (e) => {
      setSelectedImage(e.target?.result as string);
    };
    reader.readAsDataURL(file);
    
    // Reset previous results
    setAnalysisResult(null);
    setAnalysisStage('idle');
  };

  const handleAnalyze = async () => {
    if (!selectedImage || !selectedFile) return;

    setIsAnalyzing(true);
    setAnalysisStage('analyzing');
    setError(null);

    try {
      const result = await analyzeImage(selectedImage, selectedFile.name);
      setAnalysisResult(result);
      setAnalysisStage('complete');
    } catch (err) {
      setError('Analysis failed. Please try again.');
      setAnalysisStage('error');
    } finally {
      setIsAnalyzing(false);
    }
  };

  const handleSendToRescue = () => {
    if (analysisResult && onSendToRescue) {
      onSendToRescue(analysisResult);
    }
    resetAndClose();
  };

  const resetAndClose = () => {
    setSelectedImage(null);
    setSelectedFile(null);
    setAnalysisResult(null);
    setError(null);
    setAnalysisStage('idle');
    onClose();
  };

  const getDisasterIcon = (type: string) => {
    switch(type) {
      case 'flood': return <Droplets className="text-blue-500" size={32} />;
      case 'fire': return <Flame className="text-orange-500" size={32} />;
      case 'earthquake': return <Activity className="text-yellow-500" size={32} />;
      case 'cyclone': return <AlertTriangle className="text-purple-500" size={32} />;
      case 'building_collapse': return <FileWarning className="text-red-500" size={32} />;
      case 'landslide': return <AlertTriangle className="text-brown-500" size={32} />;
      case 'none': return <CheckCircle className="text-green-500" size={32} />;
      default: return <HelpCircle className="text-gray-500" size={32} />;
    }
  };

  const getSeverityColor = (severity: string) => {
    switch(severity) {
      case 'critical': return 'bg-red-500/20 text-red-500 border-red-500/30';
      case 'high': return 'bg-orange-500/20 text-orange-500 border-orange-500/30';
      case 'medium': return 'bg-yellow-500/20 text-yellow-500 border-yellow-500/30';
      case 'low': return 'bg-green-500/20 text-green-500 border-green-500/30';
      default: return 'bg-gray-500/20 text-gray-500 border-gray-500/30';
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/90 flex items-center justify-center z-50 backdrop-blur-sm"
        onClick={resetAndClose}
      >
        <motion.div
          initial={{ scale: 0.9, y: 20 }}
          animate={{ scale: 1, y: 0 }}
          exit={{ scale: 0.9, y: 20 }}
          className="bg-gray-900 w-full max-w-2xl rounded-xl border border-cyan-500/50 overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex justify-between items-center p-6 border-b border-gray-800">
            <div className="flex items-center gap-3">
              <Camera className="text-cyan-500" size={24} />
              <h2 className="text-2xl font-bold text-cyan-500">AI Disaster Analysis</h2>
            </div>
            <button 
              onClick={resetAndClose}
              className="hover:bg-gray-800 p-2 rounded-lg transition-all"
            >
              <X size={20} className="text-gray-400" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6">
            {/* Upload Area - Only show when no image selected */}
            {!selectedImage && (
              <div
                className={`relative border-2 border-dashed rounded-xl p-12 text-center transition-all ${
                  dragActive 
                    ? 'border-cyan-500 bg-cyan-500/10' 
                    : 'border-gray-700 hover:border-cyan-500/50'
                }`}
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
              >
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/gif,image/webp"
                  onChange={handleImageSelect}
                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                />
                <Upload size={48} className="mx-auto mb-4 text-gray-400" />
                <p className="text-lg mb-2">Drag & drop or click to upload</p>
                <p className="text-sm text-gray-400">Supports: JPG, PNG, GIF, WEBP (Max 10MB)</p>
              </div>
            )}

            {/* Error Message */}
            {error && (
              <div className="mt-4 p-4 bg-red-500/10 border border-red-500/30 rounded-lg">
                <p className="text-red-500 flex items-center gap-2">
                  <XCircle size={16} />
                  {error}
                </p>
              </div>
            )}

            {/* Image Preview - Show when image selected */}
            {selectedImage && (
              <div className="space-y-6">
                <div className="relative rounded-lg overflow-hidden border border-cyan-500/30">
                  <img 
                    src={selectedImage} 
                    alt="Uploaded for analysis" 
                    className="w-full max-h-96 object-contain bg-black/50"
                  />
                  <button
                    onClick={() => {
                      setSelectedImage(null);
                      setSelectedFile(null);
                      setAnalysisResult(null);
                      setAnalysisStage('idle');
                    }}
                    className="absolute top-3 right-3 bg-black/70 hover:bg-black p-2 rounded-lg transition-all"
                  >
                    <X size={16} />
                  </button>
                </div>

                {/* Analysis Controls */}
                {analysisStage === 'idle' && (
                  <button
                    onClick={handleAnalyze}
                    className="w-full bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-700 hover:to-blue-700 text-white font-bold py-4 px-6 rounded-lg flex items-center justify-center gap-2 text-lg"
                  >
                    <Camera size={20} />
                    Analyze Image with AI
                  </button>
                )}

                {/* Loading State */}
                {analysisStage === 'analyzing' && (
                  <div className="text-center py-8">
                    <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-cyan-500 mx-auto mb-4"></div>
                    <p className="text-cyan-500 text-lg font-medium">Analyzing image...</p>
                    <p className="text-sm text-gray-400 mt-2">This may take a few seconds</p>
                  </div>
                )}

                {/* Results */}
                {analysisResult && analysisStage === 'complete' && (
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="space-y-6"
                  >
                    {/* Disaster Type & Confidence */}
                    <div className="flex items-center justify-between p-6 bg-black/30 rounded-lg border border-cyan-500/30">
                      <div className="flex items-center gap-4">
                        {getDisasterIcon(analysisResult.disasterType)}
                        <div>
                          <p className="text-sm text-gray-400">Detected Disaster</p>
                          <p className="text-3xl font-bold capitalize">
                            {analysisResult.disasterType === 'building_collapse' ? 'Building Collapse' : 
                             analysisResult.disasterType === 'none' ? 'No Disaster' :
                             analysisResult.disasterType}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-sm text-gray-400">Confidence</p>
                        <p className={`text-3xl font-bold ${
                          analysisResult.confidence > 80 ? 'text-green-500' :
                          analysisResult.confidence > 60 ? 'text-yellow-500' :
                          'text-red-500'
                        }`}>
                          {analysisResult.confidence}%
                        </p>
                      </div>
                    </div>

                    {/* Visual Evidence */}
                    {analysisResult.visualCues.length > 0 && (
                      <div className="p-4 bg-black/30 rounded-lg border border-cyan-500/30">
                        <p className="text-sm text-gray-400 mb-2">Visual Evidence Detected</p>
                        <div className="flex flex-wrap gap-2">
                          {analysisResult.visualCues.map((cue, index) => (
                            <span key={index} className="px-3 py-1 bg-cyan-500/20 text-cyan-500 rounded-full text-sm">
                              {cue}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Key Metrics Grid - SIMPLE 2x2 */}
<div className="grid grid-cols-2 gap-4">
  {/* Trapped People Card */}
  <div className={`p-4 rounded-lg border ${analysisResult.trappedPeople > 0 ? 'bg-red-500/10 border-red-500/30' : 'bg-gray-800/50 border-gray-700'}`}>
    <Users className={`mb-2 ${analysisResult.trappedPeople > 0 ? 'text-red-500' : 'text-gray-400'}`} size={24} />
    <p className="text-sm text-gray-400">Trapped People</p>
    <p className={`text-2xl font-bold ${analysisResult.trappedPeople > 0 ? 'text-red-500' : 'text-gray-400'}`}>
      {analysisResult.trappedPeople > 0 ? analysisResult.trappedPeople : 'None detected'}
    </p>
  </div>

  {/* Damage Severity Card */}
  <div className={`p-4 rounded-lg border ${getSeverityColor(analysisResult.damageSeverity)}`}>
    <AlertTriangle size={24} className="mb-2" />
    <p className="text-sm text-gray-400">Damage Severity</p>
    <p className="text-2xl font-bold capitalize">{analysisResult.damageSeverity}</p>
  </div>

  {/* Fire Detected Card */}
  <div className={`p-4 rounded-lg border ${analysisResult.fireDetected ? 'bg-orange-500/10 border-orange-500/30' : 'bg-gray-800/50 border-gray-700'}`}>
    <Flame size={24} className={analysisResult.fireDetected ? 'text-orange-500' : 'text-gray-400'} />
    <p className="text-sm text-gray-400">Fire Detected</p>
    <p className={`text-2xl font-bold ${analysisResult.fireDetected ? 'text-orange-500' : 'text-gray-400'}`}>
      {analysisResult.fireDetected ? 'YES' : 'NO'}
    </p>
  </div>

  {/* Collapsed Buildings Card */}
  <div className={`p-4 rounded-lg border ${analysisResult.collapsedBuildings ? 'bg-red-500/10 border-red-500/30' : 'bg-gray-800/50 border-gray-700'}`}>
    <Activity size={24} className={analysisResult.collapsedBuildings ? 'text-red-500' : 'text-gray-400'} />
    <p className="text-sm text-gray-400">Collapsed Buildings</p>
    <p className={`text-2xl font-bold ${analysisResult.collapsedBuildings ? 'text-red-500' : 'text-gray-400'}`}>
      {analysisResult.collapsedBuildings ? 'YES' : 'NO'}
    </p>
  </div>
</div>
                    {/* AI Recommendations */}
                    <div className="p-6 bg-cyan-500/10 rounded-lg border border-cyan-500/30">
                      <p className="text-lg font-bold text-cyan-500 mb-4 flex items-center gap-2">
                        <Activity size={20} />
                        AI Recommendations
                      </p>
                      <ul className="space-y-3">
                        {analysisResult.recommendations.map((rec, i) => (
                          <li key={i} className="flex items-start gap-3 text-gray-300">
                            <span className="text-cyan-500 mt-1">•</span>
                            <span>{rec}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex gap-4">
                      <button
                        onClick={handleSendToRescue}
                        className="flex-1 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white font-bold py-4 px-6 rounded-lg flex items-center justify-center gap-2 text-lg"
                      >
                        <CheckCircle size={20} />
                        Send to Rescue Command
                      </button>
                      <button
                        onClick={() => {
                          setSelectedImage(null);
                          setSelectedFile(null);
                          setAnalysisResult(null);
                          setAnalysisStage('idle');
                        }}
                        className="flex-1 bg-gray-700 hover:bg-gray-600 text-white font-bold py-4 px-6 rounded-lg flex items-center justify-center gap-2 text-lg"
                      >
                        <RefreshCw size={20} />
                        Analyze New Image
                      </button>
                    </div>
                  </motion.div>
                )}
              </div>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default ImageAnalysisModal;